import { useIntl } from '@umijs/max';
import { Menu, type MenuProps } from 'antd';
import { createStyles } from 'antd-style';
import React, { useMemo, useState } from 'react';
import {
  ApiOutlined,
  DesktopOutlined,
  IdcardOutlined,
  LockOutlined,
} from '@/components/icon/tabler';
import ApiKeysView from './components/apikeys';
import BaseView from './components/base';
import SecurityView from './components/security';
import SessionsView from './components/sessions';

const SIDER_WIDTH = 200;

const useStyles = createStyles(({ token, css }) => ({
  root: css`
    display: flex;
    width: 100%;
    height: 100%;
  `,
  sider: css`
    width: ${SIDER_WIDTH}px;
    flex: 0 0 ${SIDER_WIDTH}px;
    height: 100%;
    background: ${token.colorFillTertiary};
    border-right: 1px solid ${token.colorBorderSecondary};
    display: flex;
    flex-direction: column;
    overflow-y: auto;
  `,
  menu: css`
    border-inline-end: none;
    background: transparent;
    flex: 1 1 auto;

    .ant-menu-item-group-title {
      font-size: 11px;
      letter-spacing: 0.4px;
      text-transform: uppercase;
      color: ${token.colorTextSecondary};
      padding: ${token.paddingXS}px ${token.padding}px ${token.paddingXXS}px !important;
    }

    .ant-menu-item {
      height: 32px !important;
      line-height: 32px !important;
      margin: 1px ${token.paddingXS}px !important;
      padding: 0 ${token.padding}px !important;
      width: calc(100% - ${token.paddingXS * 2}px) !important;
      border-radius: ${token.borderRadius}px;
      font-size: 13px;
    }
    .ant-menu-item .anticon {
      font-size: 14px;
    }
  `,
  main: css`
    flex: 1;
    min-width: 0;
    height: 100%;
    background: ${token.colorBgElevated};
    overflow-y: auto;
  `,
  content: css`
    padding: ${token.paddingXL}px ${token.paddingXL}px;
  `,
}));

const Settings: React.FC = () => {
  const intl = useIntl();
  const { styles } = useStyles();
  const [selectedKey, setSelectedKey] = useState<string>('base');

  const menuItems: MenuProps['items'] = useMemo(
    () => [
      {
        type: 'group',
        label: intl.formatMessage({
          id: 'pages.account.settings.group.account',
          defaultMessage: '账户',
        }),
        children: [
          {
            key: 'base',
            icon: <IdcardOutlined />,
            label: intl.formatMessage({
              id: 'pages.account.settings.menu.base',
              defaultMessage: '基本设置',
            }),
          },
          {
            key: 'security',
            icon: <LockOutlined />,
            label: intl.formatMessage({
              id: 'pages.account.settings.menu.security',
              defaultMessage: '安全设置',
            }),
          },
        ],
      },
      {
        type: 'group',
        label: intl.formatMessage({
          id: 'pages.account.settings.group.api',
          defaultMessage: '调用与配额',
        }),
        children: [
          {
            key: 'apikeys',
            icon: <ApiOutlined />,
            label: intl.formatMessage({
              id: 'pages.account.settings.menu.apikeys',
              defaultMessage: 'API Key',
            }),
          },
        ],
      },
      {
        type: 'group',
        label: intl.formatMessage({
          id: 'pages.account.settings.group.devices',
          defaultMessage: '设备',
        }),
        children: [
          {
            key: 'sessions',
            icon: <DesktopOutlined />,
            label: intl.formatMessage({
              id: 'pages.account.settings.menu.sessions',
              defaultMessage: '会话管理',
            }),
          },
        ],
      },
    ],
    [intl],
  );

  const view = (() => {
    switch (selectedKey) {
      case 'security':
        return <SecurityView />;
      case 'apikeys':
        return <ApiKeysView />;
      case 'sessions':
        return <SessionsView />;
      default:
        return <BaseView />;
    }
  })();

  return (
    <div className={styles.root}>
      <aside className={styles.sider}>
        <Menu
          mode="inline"
          className={styles.menu}
          items={menuItems}
          selectedKeys={[selectedKey]}
          onClick={({ key }) => setSelectedKey(key)}
        />
      </aside>
      <section className={styles.main}>
        <div className={styles.content}>{view}</div>
      </section>
    </div>
  );
};

export default Settings;
